import { elementOf, type GameState, type Move } from '@archon3d/core';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { IPointerEvent } from '@babylonjs/core/Events/deviceInputEvents';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import type { GameSession, SessionSnapshot } from '../../game/session';
import type { GameTablets } from '../tablets/gameTablets';
import type { Highlights } from './highlights';
import type { PieceLayer } from './pieces';
import { createSpellEffects } from './spellEffects';
import { afflictionOf, createStatusSigns } from './statusSigns';
import { cellToWorld, challengeStances } from './layout';
import { targetsWithOdds, threatsToMover } from './threatHints';
import type { BoardTiles } from './tiles';
import { nearestPoint, nextInDirection, type Point } from '../../input/spatialNavigation';

export interface Cell {
  x: number;
  y: number;
}

export interface BoardViewParts {
  tiles: BoardTiles;
  pieces: PieceLayer;
  highlights: Highlights;
  tablets: GameTablets;
  /** Called when a new piece mesh appears (to register shadows/reflections). */
  onPieceMesh?: (mesh: Mesh) => void;
}

export interface BoardView {
  /** Odds on attack targets and warning rings under threatened pieces: a setting, on by default. */
  setThreatHints(on: boolean): void;
  /** What the hints show right now, for tests. */
  hints(): { labels: string[]; threatened: number[]; signs: string[] };
  /** The cell under the cursor: where the mouse points, or where a gamepad has stepped to. */
  hover(): Cell | null;
  setHover(cell: Cell | null): void;
  /**
   * Steps the cursor one cell in a screen direction (y down). A cursor that is not on the board
   * yet starts from the selected piece, else from the cell nearest the mouse pointer.
   */
  stepHover(direction: Point): boolean;
  /** Picks the cell under the cursor, as a click on it would. */
  pickHover(): void;
  /** Where a cell is drawn on the screen, in pixels, for tests. */
  screenPointOf(cell: Cell): Point | null;
  detach(): void;
}

/**
 * The squares a move went from and to, for the last-move marker (x-aaa-gap#18): a step or a
 * teleport both, a spell on a piece that piece's, one that places something where it places it;
 * nothing for a pass, a draw or a spell on the whole board.
 */
export function squaresOf(before: GameState | null, move: Move | null): Cell[] {
  const at = (id: number): Cell[] => {
    const piece = before?.pieces.find((p) => p.id === id);
    return piece ? [{ x: piece.x, y: piece.y }] : [];
  };
  if (!move) return [];
  switch (move.type) {
    case 'move':
      return [...at(move.pieceId), { x: move.x, y: move.y }];
    case 'spell':
      switch (move.spell) {
        case 'shift-time':
          return [];
        case 'teleport':
          return [...at(move.pieceId), { x: move.x, y: move.y }];
        case 'exchange':
          return [...at(move.pieceId), ...at(move.otherId)];
        case 'revive':
        case 'summon-elemental':
          return [{ x: move.x, y: move.y }];
        default:
          return at(move.pieceId);
      }
    default:
      return [];
  }
}

/** Keeps the meshes in step with the game session and turns taps/clicks/VR rays into picks. */
export function attachBoardView(
  scene: Scene,
  session: GameSession,
  parts: BoardViewParts,
): BoardView {
  let luminosityPhase = -1;
  let previous: GameState | null = null;
  /** The move whose spell was last shown: each is shown once. */
  let shownMove: Move | null = null;
  let threatHints = true;
  let threatened: number[] = [];
  let hover: Cell | null = null;
  const hold = () => session.hold();
  const { board, roster } = session.getSnapshot().ctx;
  const turntable = parts.tiles.tiles.parent as TransformNode | null;
  const floorCells: Cell[] = board.cells
    .filter((c) => c.kind === 'floor')
    .map((c) => ({ x: c.x, y: c.y }));

  const setHover = (cell: Cell | null) => {
    if (cell?.x === hover?.x && cell?.y === hover?.y) return;
    hover = cell;
    parts.highlights.setHover(cell);
  };

  /** The cell a pick landed on: a floor tile, or the tile under a piece. */
  const cellOfPick = (mesh: AbstractMesh | null | undefined, instance: number): Cell | null => {
    if (!mesh) return null;
    const pieceId = parts.pieces.pieceIdOf(mesh);
    if (pieceId !== undefined) {
      const piece = session.getSnapshot().state.pieces.find((p) => p.id === pieceId);
      return piece ? { x: piece.x, y: piece.y } : null;
    }
    if (mesh === parts.tiles.tiles && instance >= 0) {
      const cell = parts.tiles.floorCells[instance];
      return cell ? { x: cell.x, y: cell.y } : null;
    }
    return null;
  };

  const pickable = (mesh: AbstractMesh) =>
    mesh === parts.tiles.tiles || parts.pieces.pieceIdOf(mesh) !== undefined;

  const screenPointOf = (cell: Cell): Point | null => {
    const camera = scene.activeCamera;
    if (!camera) return null;
    const engine = scene.getEngine();
    const world = turntable
      ? Vector3.TransformCoordinates(cellToWorld(board, cell.x, cell.y), turntable.getWorldMatrix())
      : cellToWorld(board, cell.x, cell.y);
    const p = Vector3.Project(
      world,
      Matrix.IdentityReadOnly,
      scene.getTransformMatrix(),
      camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight()),
    );
    return { x: p.x, y: p.y };
  };
  // The effects hang under the turntable with the tiles and pieces, so they turn with the board.
  const effects = createSpellEffects(
    scene,
    board,
    parts.tiles.tiles.parent as TransformNode | null,
  );
  // The signs hang under the turntable with the pieces, so they turn with the board.
  const signs = createStatusSigns(scene, parts.tiles.tiles.parent as TransformNode | null);
  // A taken piece raises a puff of its side's colour where it falls (x-aaa-gap#8).
  const stopFalls = parts.pieces.onFall((fall) => effects.capture(fall.at, fall.color));

  /** What a spell did to the board, and a summoned creature leaving it. */
  const showSpell = (before: GameState, after: GameState, move: Move | null) => {
    if (move?.type === 'spell') effects.play(move, before, after);
    for (const p of before.pieces) {
      if (!roster[p.character]?.summoned || after.pieces.some((q) => q.id === p.id)) continue;
      const element = elementOf(p.character);
      if (!element) continue;
      // An attacker waits on its victim's square, off to the side it came in on (see layout.ts);
      // the puff rises from there. A summoned creature attacks from where it was conjured.
      const battle = before.pendingBattle;
      const at =
        battle?.attackerId === p.id
          ? challengeStances(board, { x: p.x, y: p.y }, { x: battle.x, y: battle.y }).attacker
          : cellToWorld(board, p.x, p.y);
      effects.dismiss(element, at);
    }
  };

  const apply = (snapshot: SessionSnapshot) => {
    const { state } = snapshot;
    if (state.luminosity.phase !== luminosityPhase) {
      luminosityPhase = state.luminosity.phase;
      parts.tiles.setLuminosityPhase(luminosityPhase);
    }
    if (state !== previous) {
      parts.pieces.sync({ state, previous, lastMove: snapshot.lastMove, hold });
      // The session publishes new states under the same last move (a Card clash begun, its
      // cards turned); each used to cast the spell's effect again (client-board:bugs#5).
      const fresh = snapshot.lastMove !== shownMove ? snapshot.lastMove : null;
      if (previous) showSpell(previous, state, fresh);
      if (snapshot.lastMove !== shownMove)
        parts.highlights.setLastMove(squaresOf(previous, snapshot.lastMove));
      shownMove = snapshot.lastMove;
      previous = state;
    }
    const pieceCells = snapshot.pieceTargets
      .map((id) => state.pieces.find((p) => p.id === id))
      .filter((p): p is NonNullable<typeof p> => p !== undefined)
      .map((p) => ({ x: p.x, y: p.y }));
    parts.highlights.setTargets(
      threatHints ? targetsWithOdds(snapshot) : snapshot.cellTargets,
      pieceCells,
      (x, y) => {
        const piece = state.pieces.find((p) => p.x === x && p.y === y);
        const top = piece ? parts.pieces.topOf(piece.id) : undefined;
        return piece && top !== undefined
          ? { top, signed: afflictionOf(piece) !== null }
          : undefined;
      },
    );
    const selected = state.pieces.find((p) => p.id === snapshot.selectedPieceId);
    parts.highlights.setSelection(selected ? { x: selected.x, y: selected.y } : null);
    // Several enemies may threaten one piece: one ring per piece.
    // Curse, Freeze and Imprison all leave a piece looking exactly as it did, so the sign is the
    // whole of the feedback. A piece under two of them shows only the heavier.
    signs.set(
      state.pieces.flatMap((piece) => {
        const affliction = afflictionOf(piece);
        const at = parts.pieces.positionOf(piece.id);
        const top = parts.pieces.topOf(piece.id);
        return affliction && at ? [{ pieceId: piece.id, affliction, at, top }] : [];
      }),
    );
    // And a ring on the square itself, so the sign overhead is never read as the square behind.
    parts.highlights.setAfflicted(
      state.pieces.filter((piece) => afflictionOf(piece) !== null).map((p) => ({ x: p.x, y: p.y })),
    );
    const threats = threatHints ? threatsToMover(snapshot) : [];
    const cells = new Map(threats.map((t) => [t.target.id, { x: t.target.x, y: t.target.y }]));
    threatened = [...cells.keys()];
    parts.highlights.setThreats([...cells.values()]);
  };

  apply(session.getSnapshot());
  const unsubscribe = session.subscribe(() => apply(session.getSnapshot()));

  /** In the headset, the controller whose laser the hover ring follows. */
  let laser: number | null = null;
  const pointer = scene.onPointerObservable.add((info) => {
    const event = info.event as IPointerEvent;
    const fromLaser = event.pointerType === 'xr';
    if (fromLaser && info.type === PointerEventTypes.POINTERDOWN) laser = event.pointerId;
    if (info.type === PointerEventTypes.POINTERMOVE) {
      if (fromLaser) {
        // A laser brings its own pick, made along the controller's ray, so the ring sits under
        // its dot. With one in each hand it follows the hand that last pulled its trigger (or the
        // first to point), where two would move it between them every frame.
        laser ??= event.pointerId;
        if (event.pointerId !== laser) return;
        const pick = info.pickInfo;
        setHover(pick?.hit ? cellOfPick(pick.pickedMesh, pick.thinInstanceIndex) : null);
        return;
      }
      // The scene skips move picking for speed; one ray against the tiles and pieces is cheap.
      const under = scene.pick(scene.pointerX, scene.pointerY, pickable);
      setHover(under?.hit ? cellOfPick(under.pickedMesh, under.thinInstanceIndex) : null);
      return;
    }
    if (info.type !== PointerEventTypes.POINTERTAP) return;
    const pick = info.pickInfo;
    if (!pick?.hit || !pick.pickedMesh) return;
    if (parts.tablets.tap(pick)) return;
    const pieceId = parts.pieces.pieceIdOf(pick.pickedMesh);
    if (pieceId !== undefined) {
      session.pickPiece(pieceId);
      return;
    }
    if (pick.pickedMesh === parts.tiles.tiles && pick.thinInstanceIndex >= 0) {
      const cell = parts.tiles.floorCells[pick.thinInstanceIndex];
      if (cell) session.pickCell(cell.x, cell.y);
    }
  });

  // A mouse that leaves the picture points at no square any more.
  const canvas = scene.getEngine().getRenderingCanvas();
  const leave = (event: PointerEvent) => {
    if (event.pointerType === 'mouse') setHover(null);
  };
  canvas?.addEventListener('pointerleave', leave);

  const frame = scene.onBeforeRenderObservable.add(() => {
    const dt = scene.getEngine().getDeltaTime() / 1000;
    parts.pieces.update(dt);
    parts.highlights.update(dt, scene.activeCamera);
    signs.update(
      scene.activeCamera,
      (id) => parts.pieces.positionOf(id),
      (id) => parts.pieces.topOf(id),
    );
    effects.update(dt);
  });

  return {
    setThreatHints(on) {
      if (on === threatHints) return;
      threatHints = on;
      apply(session.getSnapshot());
    },
    hints: () => ({
      labels: parts.highlights.labels(),
      threatened: threatened.slice(),
      signs: signs.texts(),
    }),
    hover: () => hover,
    setHover,
    stepHover(direction) {
      const points = floorCells.map(screenPointOf);
      if (points.some((p) => p === null)) return false;
      const screen = points as Point[];
      const from: Point | null = hover ? (screenPointOf(hover) ?? null) : null;
      if (!from) {
        // First step: from the selected piece, else the cell nearest the mouse pointer.
        const snapshot = session.getSnapshot();
        const selected = snapshot.state.pieces.find((p) => p.id === snapshot.selectedPieceId);
        const start = selected
          ? floorCells.findIndex((c) => c.x === selected.x && c.y === selected.y)
          : nearestPoint(screen, { x: scene.pointerX, y: scene.pointerY });
        if (start === null || start < 0) return false;
        setHover(floorCells[start]!);
        return true;
      }
      const next = nextInDirection(
        screen,
        from,
        direction,
        (i) => floorCells[i]!.x === hover!.x && floorCells[i]!.y === hover!.y,
      );
      if (next === null) return false;
      setHover(floorCells[next]!);
      return true;
    },
    pickHover() {
      if (hover) session.pickCell(hover.x, hover.y);
    },
    screenPointOf,
    detach() {
      unsubscribe();
      scene.onPointerObservable.remove(pointer);
      canvas?.removeEventListener('pointerleave', leave);
      scene.onBeforeRenderObservable.remove(frame);
      stopFalls();
      effects.dispose();
      signs.dispose();
    },
  };
}
